import { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';

export interface NavProps extends ComponentPropsWithoutRef<'nav'> {
  children: ReactNode;
}

/**
 * Main navigation frame. KERN has no side navigation, so it is built from KERN tokens:
 * `nav` landmark, list, visible focus and `aria-current` for the active page.
 */
export const Nav = ({
  children,
  className,
  'aria-label': ariaLabel = 'Hauptnavigation',
  ...props
}: NavProps) => (
  <nav
    {...props}
    aria-label={ariaLabel}
    className={['vf-nav', className].filter(Boolean).join(' ')}
  >
    {children}
  </nav>
);

export interface NavListProps extends ComponentPropsWithoutRef<'ul'> {}

const NavList = ({ className, ...props }: NavListProps) => (
  <ul
    {...props}
    className={['vf-nav__list', className].filter(Boolean).join(' ')}
  />
);

export type NavItemProps = {
  /** Marks the entry as the current page (sets `aria-current="page"`). */
  current?: boolean;
  icon?: ReactNode;
  children?: ReactNode;
  className?: string;
  /**
   * Element that renders the link — inside a router e.g. react-router's `Link`, so that
   * navigation works without a full page reload. Defaults to `a`.
   */
  as?: ElementType;
} & Record<string, unknown>;

const NavItem = ({
  current,
  icon,
  children,
  className,
  as: Component = 'a',
  ...props
}: NavItemProps) => (
  <li>
    <Component
      {...props}
      aria-current={current ? 'page' : undefined}
      className={['vf-nav__item', className].filter(Boolean).join(' ')}
    >
      {icon && <span aria-hidden="true">{icon}</span>}
      <span>{children}</span>
    </Component>
  </li>
);

export interface NavSectionProps extends ComponentPropsWithoutRef<'p'> {}

const NavSection = ({ className, ...props }: NavSectionProps) => (
  <p
    {...props}
    className={['kern-label', 'kern-label--small', 'vf-nav__section', className]
      .filter(Boolean)
      .join(' ')}
  />
);

Nav.List = NavList;
Nav.Item = NavItem;
Nav.Section = NavSection;
